import { useState } from "react";
import { handleExternalNav } from "@/lib/utils";
import {
  Plus, Trash2, GripVertical, Music, ExternalLink,
  ChevronDown, ChevronUp, FileText
} from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { tokens } from "@/design-system/tokens";

export const SetlistEditor = ({ setlist = [], onChange }) => {
  const [expandedItems, setExpandedItems] = useState({});

  const addTune = () => {
    const newTune = {
      tune_name: "",
      key: "",
      tempo: "",
      notes: "",
      chart_url: "",
      audio_url: ""
    };
    onChange([...setlist, newTune]);
  };

  const updateTune = (index, field, value) => {
    const updated = [...setlist];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const removeTune = (index) => {
    onChange(setlist.filter((_, i) => i !== index));
  };

  const moveTune = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= setlist.length) return;
    
    const updated = [...setlist];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    onChange(updated);
  };

  const toggleExpanded = (index) => {
    setExpandedItems(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  return (
    <div style={{
      backgroundColor: tokens.color.cardBg,
      border: tokens.border.card,
      borderRadius: tokens.radius.lg,
      overflow: "hidden"
    }}>
      <div style={{
        paddingBottom: `${tokens.space.md}px`,
        borderBottom: tokens.border.inner,
        paddingLeft: `${tokens.space.lg}px`,
        paddingRight: `${tokens.space.lg}px`,
        paddingTop: `${tokens.space.lg}px`
      }}>
        <div style={{ fontSize: "18px", display: "flex", alignItems: "center", gap: tokens.space.md, color: tokens.color.text, fontFamily: tokens.font.display, fontWeight: 600, margin: 0 }}>
          <Music style={{ height: "20px", width: "20px", color: tokens.color.orange }} />
          Setlist
          <span style={{ fontSize: "12px", fontWeight: 400, color: tokens.color.textMuted }}>
            ({setlist.length} tune{setlist.length !== 1 ? 's' : ''})
          </span>
        </div>
      </div>
      <div style={{ padding: `${tokens.space.lg}px`, display: "flex", flexDirection: "column", gap: `${tokens.space.md}px` }}>
        {setlist.length === 0 ? (
          <p style={{ fontSize: "12px", color: tokens.color.textMuted, textAlign: "center", paddingTop: tokens.space.lg, paddingBottom: tokens.space.lg, margin: 0 }}>
            No tunes added yet. Click below to add your first tune.
          </p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: tokens.space.md }}>
            {setlist.map((tune, index) => (
              <Collapsible
                key={index}
                open={expandedItems[index]}
                onOpenChange={() => toggleExpanded(index)}
              >
                <div style={{ border: tokens.border.inner, borderRadius: tokens.radius.xl, backgroundColor: tokens.color.innerBg }}>
                  {/* Collapsed Header */}
                  <div style={{ display: "flex", alignItems: "center", gap: tokens.space.md, padding: tokens.space.md }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                      <button
                        type="button"
                        style={{
                          height: "20px",
                          width: "20px",
                          backgroundColor: "transparent",
                          border: "none",
                          cursor: index === 0 ? "not-allowed" : "pointer",
                          color: tokens.color.textMuted,
                          opacity: index === 0 ? 0.5 : 1,
                          transition: "color 150ms ease"
                        }}
                        onClick={() => moveTune(index, -1)}
                        disabled={index === 0}
                        onMouseEnter={(e) => !index === 0 && (e.currentTarget.style.color = tokens.color.text)}
                        onMouseLeave={(e) => e.currentTarget.style.color = tokens.color.textMuted}
                      >
                        <ChevronUp style={{ height: "12px", width: "12px" }} />
                      </button>
                      <button
                        type="button"
                        style={{
                          height: "20px",
                          width: "20px",
                          backgroundColor: "transparent",
                          border: "none",
                          cursor: index === setlist.length - 1 ? "not-allowed" : "pointer",
                          color: tokens.color.textMuted,
                          opacity: index === setlist.length - 1 ? 0.5 : 1,
                          transition: "color 150ms ease"
                        }}
                        onClick={() => moveTune(index, 1)}
                        disabled={index === setlist.length - 1}
                        onMouseEnter={(e) => !(index === setlist.length - 1) && (e.currentTarget.style.color = tokens.color.text)}
                        onMouseLeave={(e) => e.currentTarget.style.color = tokens.color.textMuted}
                      >
                        <ChevronDown style={{ height: "12px", width: "12px" }} />
                      </button>
                    </div>

                    <span style={{ fontSize: "10px", color: tokens.color.textMuted, width: "24px" }}>{index + 1}.</span>

                    <input
                      type="text"
                      value={tune.tune_name}
                      onChange={(e) => updateTune(index, "tune_name", e.target.value)}
                      style={{
                        flex: 1,
                        height: "32px",
                        fontSize: "12px",
                        backgroundColor: tokens.color.innerBg,
                        color: tokens.color.text,
                        border: tokens.border.inner,
                        borderRadius: tokens.radius.md,
                        padding: `0 ${tokens.space.md}px`,
                        fontFamily: tokens.font.body
                      }}
                      data-testid={`setlist-tune-${index}`}
                    />

                    <input
                      type="text"
                      value={tune.key}
                      onChange={(e) => updateTune(index, "key", e.target.value)}
                      style={{
                        width: "80px",
                        height: "32px",
                        fontSize: "12px",
                        backgroundColor: tokens.color.innerBg,
                        color: tokens.color.text,
                        border: tokens.border.inner,
                        borderRadius: tokens.radius.md,
                        padding: `0 ${tokens.space.md}px`,
                        fontFamily: tokens.font.body
                      }}
                      data-testid={`setlist-key-${index}`}
                    />

                    <CollapsibleTrigger asChild>
                      <button
                        type="button"
                        style={{
                          height: "32px",
                          backgroundColor: "transparent",
                          border: "none",
                          cursor: "pointer",
                          color: tokens.color.textMuted,
                          transition: "color 150ms ease"
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.color = tokens.color.text}
                        onMouseLeave={(e) => e.currentTarget.style.color = tokens.color.textMuted}
                      >
                        {expandedItems[index] ? (
                          <ChevronUp style={{ height: "16px", width: "16px" }} />
                        ) : (
                          <ChevronDown style={{ height: "16px", width: "16px" }} />
                        )}
                      </button>
                    </CollapsibleTrigger>

                    <button
                      type="button"
                      style={{
                        height: "32px",
                        width: "32px",
                        backgroundColor: "transparent",
                        border: "none",
                        cursor: "pointer",
                        color: tokens.color.red,
                        transition: "color 150ms ease"
                      }}
                      onClick={() => removeTune(index)}
                      onMouseEnter={(e) => e.currentTarget.style.color = tokens.color.red}
                      onMouseLeave={(e) => e.currentTarget.style.color = tokens.color.red}
                    >
                      <Trash2 style={{ height: "16px", width: "16px" }} />
                    </button>
                  </div>

                  {/* Expanded Details */}
                  <CollapsibleContent>
                    <div style={{ paddingLeft: tokens.space.md, paddingRight: tokens.space.md, paddingBottom: tokens.space.md, paddingTop: 0, display: "flex", flexDirection: "column", gap: tokens.space.md, borderTop: tokens.border.inner, marginTop: tokens.space.md }}>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: tokens.space.md, paddingTop: tokens.space.md }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: tokens.space.xs }}>
                          <label style={{ fontSize: "10px", color: tokens.color.textMuted }}>Tempo</label>
                          <input
                            type="text"
                            value={tune.tempo}
                            onChange={(e) => updateTune(index, "tempo", e.target.value)}
                            style={{
                              height: "32px",
                              fontSize: "12px",
                              backgroundColor: tokens.color.innerBg,
                              color: tokens.color.text,
                              border: tokens.border.inner,
                              borderRadius: tokens.radius.md,
                              padding: `0 ${tokens.space.md}px`,
                              fontFamily: tokens.font.body
                            }}
                          />
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: tokens.space.xs }}>
                          <label style={{ fontSize: "10px", color: tokens.color.textMuted }}>Chart URL</label>
                          <div style={{ display: "flex", gap: tokens.space.xs }}>
                            <input
                              type="text"
                              value={tune.chart_url}
                              onChange={(e) => updateTune(index, "chart_url", e.target.value)}
                              style={{
                                flex: 1,
                                height: "32px",
                                fontSize: "12px",
                                backgroundColor: tokens.color.innerBg,
                                borderColor: tokens.color.innerBorder,
                                color: tokens.color.text,
                                border: `1px solid ${tokens.color.innerBorder}`,
                                borderRadius: tokens.radius.md,
                                padding: `0 ${tokens.space.md}`,
                                fontFamily: "Geist, sans-serif"
                              }}
                            />
                            {tune.chart_url && (
                              <a
                                href={tune.chart_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={handleExternalNav}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: "32px",
                                  height: "32px",
                                  borderRadius: tokens.radius.md,
                                  border: `1px solid ${tokens.color.innerBorder}`,
                                  color: tokens.color.textMuted,
                                  transition: "all 150ms ease",
                                  cursor: "pointer"
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = tokens.color.innerBg;
                                  e.currentTarget.style.color = tokens.color.text;
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = "transparent";
                                  e.currentTarget.style.color = tokens.color.textMuted;
                                }}
                              >
                                <ExternalLink style={{ height: "12px", width: "12px" }} />
                              </a>
                            )}
                          </div>
                        </div>
                      </div>

                      <div style={{ display: "flex", flexDirection: "column", gap: tokens.space.xs }}>
                        <label style={{ fontSize: "10px", color: tokens.color.textMuted }}>Audio Reference URL</label>
                        <div style={{ display: "flex", gap: tokens.space.xs }}>
                          <input
                            type="text"
                            value={tune.audio_url}
                            onChange={(e) => updateTune(index, "audio_url", e.target.value)}
                            style={{
                              flex: 1,
                              height: "32px",
                              fontSize: "12px",
                              backgroundColor: tokens.color.innerBg,
                              color: tokens.color.text,
                              border: tokens.border.inner,
                              borderRadius: tokens.radius.md,
                              padding: `0 ${tokens.space.md}px`,
                              fontFamily: tokens.font.body
                            }}
                          />
                          {tune.audio_url && (
                            <a
                              href={tune.audio_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={handleExternalNav}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "32px",
                                height: "32px",
                                borderRadius: tokens.radius.md,
                                border: `1px solid ${tokens.color.innerBorder}`,
                                color: tokens.color.textMuted,
                                transition: "all 150ms ease",
                                cursor: "pointer"
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = tokens.color.innerBg;
                                e.currentTarget.style.color = tokens.color.text;
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = "transparent";
                                e.currentTarget.style.color = designTokens.textMuted;
                              }}
                            >
                              <ExternalLink style={{ height: "12px", width: "12px" }} />
                            </a>
                          )}
                        </div>
                      </div>

                      <div style={{ display: "flex", flexDirection: "column", gap: tokens.space.xs }}>
                        <label style={{ fontSize: "10px", color: tokens.color.textMuted }}>Notes</label>
                        <textarea
                          value={tune.notes}
                          onChange={(e) => updateTune(index, "notes", e.target.value)}
                          rows={2}
                          style={{
                            fontSize: "12px",
                            backgroundColor: tokens.color.innerBg,
                            borderColor: tokens.color.innerBorder,
                            color: tokens.color.text,
                            border: `1px solid ${tokens.color.innerBorder}`,
                            borderRadius: tokens.radius.md,
                            padding: tokens.space.md,
                            fontFamily: "Geist, sans-serif",
                            resize: "vertical"
                          }}
                        />
                      </div>
                    </div>
                  </CollapsibleContent>
                </div>
              </Collapsible>
            ))}
          </div>
        )}

        <button
          type="button"
          onClick={addTune}
          style={{
            backgroundColor: "transparent",
            color: tokens.color.orange,
            border: tokens.border.card,
            borderRadius: tokens.radius.md,
            padding: `${tokens.space.xs}px ${tokens.space.md}px`,
            fontWeight: 600,
            fontSize: "13px",
            cursor: "pointer",
            fontFamily: tokens.font.body,
            width: "100%"
          }}
          data-testid="add-tune-btn"
        >
          <Plus style={{ height: "16px", width: "16px", marginRight: tokens.space.md }} />
          Add Tune
        </button>
      </div>
    </div>
  );
};

export default SetlistEditor;
